import Typography from '~/shared/components/Typography';
import Button from '~/shared/components/ButtonsV2';
import FilterIcon from '~/shared/assets/icons/filter.svg';
import CancelIcon from '~/shared/assets/icons/cancel.svg';

type ButtonVariant = 'primary' | 'secondary' | 'third' | 'text';
type ButtonSize = 'sm' | 'md' | 'md-responsive' | 'lg';
type FilterBadgeEvent = React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>;

interface FilterButtonProps {
  activeFilterCount: number;
  onOpen: () => void;
  onClearAll: () => void;
  label?: string;
  className?: string;
  size?: ButtonSize;
  variant?: ButtonVariant;
}

export default function FilterButton({
  activeFilterCount,
  onOpen,
  onClearAll,
  label = 'Filters',
  className,
  size = 'md-responsive',
  variant = 'third',
}: FilterButtonProps) {
  return (
    <Button
      size={size}
      variant={variant}
      className={`flex gap-1! items-center relative transition-all duration-800 w-auto group ${
        className ?? ''
      }`}
      leftIcon={
        <FilterIcon
          className={`size-5 aspect-square group-hover:stroke-primary-400 group-hover:stroke-2 ${
            activeFilterCount > 0 ? 'stroke-primary-400 stroke-2 ' : 'stroke-primary-900'
          }`}
        />
      }
      rightIcon={
        activeFilterCount > 0 && (
          <ClearFilterBadge count={activeFilterCount} handleClick={onClearAll} />
        )
      }
      onClick={onOpen}
    >
      <Typography variant="p3" color="text-primary-900" className="whitespace-nowrap">
        {label}
      </Typography>
    </Button>
  );
}

function ClearFilterBadge({
  count,
  handleClick,
  title = 'Clear filters',
}: {
  count: number;
  handleClick: (e?: FilterBadgeEvent) => void;
  title?: string;
}) {
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        handleClick(e);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          handleClick(e);
        }
      }}
      className="px-[6px] h-6 bg-bg rounded transition-colors duration-150 flex gap-1 items-center cursor-pointer"
      role="button"
      tabIndex={0}
      title={title}
    >
      <span
        className="leading-[16px] pt-0.5 text-gray-700 text-xs font-semibold"
        data-testid="active-filter-count"
      >
        {count}
      </span>
      <CancelIcon className="size-[17px] text-gray-600" />
    </div>
  );
}

export { ClearFilterBadge };
